import { SUBSCRIPTION_TYPES } from './subscriptions';

/** Campaign id aligned with Lambda / HGPRO (`elevated-50-off`). */
export const ELEVATED_CAMPAIGN_ID = 'elevated-50-off';

/** Figma / checkout use 1280px; theme `xl` is 1200px in tailwind.css. */
export const CAMPAIGN_WIDE_LAYOUT_CLASS = 'min-[1280px]';

/**
 * Promo display amount after percent off — same cent math as OrderSummary discount rows.
 * @param {number} listPriceDollars
 * @param {number} [percentOff=50]
 */
export function getCampaignPromoDisplayPrice(
  listPriceDollars,
  percentOff = 50,
) {
  const listCents = Math.round(listPriceDollars * 100);
  const discountCents = Math.round((listCents * percentOff) / 100);
  return `$${((listCents - discountCents) / 100).toFixed(2)}`;
}

export const ELEVATED_CAMPAIGN_PROMO_UI = Object.freeze({
  campaignId: ELEVATED_CAMPAIGN_ID,
  offerEndShort: '12/31/26',
  offerEndLong: 'December 31, 2026',
  topBanner: {
    leadBold: 'Year-end offer: ',
    offerLight: '50% off Elevated Profiles. ',
    disclaimer: 'Discount applied at checkout. Offer ends 12/31/2026',
    cta: 'Get 50% Off',
  },
  heroPromoCard: {
    headline: 'Save 50% on an Elevated profile.',
    subhead: 'Offer ends December 31, 2026',
    cta: 'Get 50% Off',
  },
  limitedTimeBadge: {
    title: 'Limited-time Offer',
    titleCheckout: 'Limited-time Offer.',
    ends: 'Ends 12/31/26',
    checkoutSuffix:
      ' Offer ends 12/31/26. Choose annual to get 50% off your entire first year.',
  },
  listPrices: {
    monthly: SUBSCRIPTION_TYPES.MONTHLY.displayPrice,
    annual: SUBSCRIPTION_TYPES.ANNUAL.displayPrice,
  },
  promoPrices: {
    monthly: getCampaignPromoDisplayPrice(SUBSCRIPTION_TYPES.MONTHLY.price),
    annual: getCampaignPromoDisplayPrice(SUBSCRIPTION_TYPES.ANNUAL.price),
    monthlySuffix: '/ month',
    annualSuffix: '/ year',
  },
  renewalDisclaimer:
    'Your subscription will auto-renew until you cancel. Monthly pricing will return to $49.95 per month after the first 12 months of your subscription. Yearly subscription pricing will return to $479.40 when your yearly subscription renews. If you choose to cancel your Elevated Profile Subscription, your subscription will end on your next billing date, and your profile will revert to the standard version.',
});

/** Comparison section footer when campaign promo is off (legacy landing). */
export const LEGACY_ELEVATED_SUBSCRIPTION_DISCLAIMER =
  'Your subscription will auto-renew until you cancel. If you choose to cancel your Elevated Profile Subscription, your subscription will end on your next billing date, and your profile will revert to the standard version.';

export const ELEVATED_CAMPAIGN_FAQ = Object.freeze([
  {
    question: 'What is the year-end offer?',
    answer: `Half off an Elevated Profile: ${ELEVATED_CAMPAIGN_PROMO_UI.promoPrices.monthly}/month (normally ${ELEVATED_CAMPAIGN_PROMO_UI.listPrices.monthly}) or ${ELEVATED_CAMPAIGN_PROMO_UI.promoPrices.annual}/year (normally ${ELEVATED_CAMPAIGN_PROMO_UI.listPrices.annual}). Sign up by ${ELEVATED_CAMPAIGN_PROMO_UI.offerEndLong} to get the discount.`,
  },
  {
    question: 'How do I get my discount?',
    answer:
      'Your discount is applied automatically at checkout once you pick a subscription plan. No code needed.',
  },
  {
    question: 'How long does the 50% off last?',
    answer: `Your 50% discount applies to the first 12 months of either plan: Yearly: ${ELEVATED_CAMPAIGN_PROMO_UI.promoPrices.annual} for your first year, then ${ELEVATED_CAMPAIGN_PROMO_UI.listPrices.annual}/year. Monthly: ${ELEVATED_CAMPAIGN_PROMO_UI.promoPrices.monthly}/month for 12 months, then ${ELEVATED_CAMPAIGN_PROMO_UI.listPrices.monthly}/month.`,
  },
  {
    question: 'I already have an Elevated Profile. Can I get the discount?',
    answer:
      'This offer is only for new Elevated Profile subscribers. Your current plan and pricing will stay the same, and you’ll keep all of your Elevated features.',
  },
]);

export function isElevatedCampaignPromoSeries(campaignId) {
  return (
    campaignId?.trim().toLowerCase() === ELEVATED_CAMPAIGN_ID.toLowerCase()
  );
}

/** Landing pricing sentence (list vs promo amounts). */
export function getElevatedPlansPricingBlurb(useCampaignPromo) {
  const { promoPrices } = ELEVATED_CAMPAIGN_PROMO_UI;
  const monthly = useCampaignPromo
    ? promoPrices.monthly
    : SUBSCRIPTION_TYPES.MONTHLY.displayPrice;
  const annual = useCampaignPromo
    ? promoPrices.annual
    : SUBSCRIPTION_TYPES.ANNUAL.displayPrice;

  return `Elevated plans are currently ${monthly} per month or ${annual} annually.`;
}

/** Numeric promo unit price for checkout order summary (per provider). */
export function getCampaignPromoUnitPrice(subscriptionId) {
  const { promoPrices } = ELEVATED_CAMPAIGN_PROMO_UI;
  const display =
    subscriptionId === SUBSCRIPTION_TYPES.ANNUAL.id
      ? promoPrices.annual
      : promoPrices.monthly;
  return Number.parseFloat(display.replace(/[$,]/g, ''));
}

/** Checkout disclosure under “Subscription Type” (standard vs campaign amounts). */
export function getCheckoutSubscriptionChargeCopy(useCampaignPromo) {
  const { promoPrices } = ELEVATED_CAMPAIGN_PROMO_UI;
  const annualAmount = useCampaignPromo
    ? promoPrices.annual
    : SUBSCRIPTION_TYPES.ANNUAL.displayPrice;
  const monthlyAmount = useCampaignPromo
    ? promoPrices.monthly
    : SUBSCRIPTION_TYPES.MONTHLY.displayPrice;

  if (useCampaignPromo) {
    return `Your credit card will be charged either ${annualAmount} yearly/per provider for the annual Healthgrades Elevated Profile Subscription or ${monthlyAmount} per month/per provider for the monthly subscription, depending on the plan you choose.`;
  }

  return `Your credit card will be charged either ${annualAmount} yearly for the annual Healthgrades Elevated Profile Subscription or ${monthlyAmount} per month for the monthly subscription, depending on the plan you choose.`;
}

/** Radio label price segment when campaign promo is active at checkout. */
export function getCheckoutPlanPriceLabel(
  subscription,
  { useCampaignPromo, isProvider },
) {
  if (!useCampaignPromo) {
    return subscription.priceLabel;
  }

  const { promoPrices } = ELEVATED_CAMPAIGN_PROMO_UI;
  const isAnnual = subscription.id === SUBSCRIPTION_TYPES.ANNUAL.id;
  const amount = isAnnual ? promoPrices.annual : promoPrices.monthly;
  const unit = isAnnual ? 'year' : 'month';
  const perProviderSuffix = isProvider ? '' : ' per provider';

  return `${amount}/${unit}${perProviderSuffix}`;
}
